import React, { useState, useEffect } from 'react';
import { API_URL } from '../utils/constants';

const AnalyticsDashboard = ({ session, projects, onClose }) => {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [sortBy, setSortBy] = useState('engagement_rate');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange, session]); // eslint-disable-line

  const loadAnalytics = async () => {
    if (!session) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics?range=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data.analytics || []);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate aggregate metrics
  const totalImpressions = analytics.reduce((sum, a) => sum + (a.impressions || 0), 0);
  const totalEngagement = analytics.reduce((sum, a) => sum + (a.likes || 0) + (a.retweets || 0) + (a.replies || 0), 0);
  const totalRevenue = analytics.reduce((sum, a) => sum + (a.revenue_generated || 0), 0);
  const avgEngagementRate = analytics.length > 0
    ? analytics.reduce((sum, a) => sum + (a.engagement_rate || 0), 0) / analytics.length
    : 0;

  // AI vs Manual comparison
  const aiPosts = analytics.filter(a => a.project_id);
  const manualPosts = analytics.filter(a => !a.project_id);
  const aiAvgEngagement = aiPosts.length > 0
    ? aiPosts.reduce((sum, a) => sum + (a.engagement_rate || 0), 0) / aiPosts.length
    : 0;
  const manualAvgEngagement = manualPosts.length > 0
    ? manualPosts.reduce((sum, a) => sum + (a.engagement_rate || 0), 0) / manualPosts.length
    : 0;

  const aiVsManualPct = manualAvgEngagement > 0
    ? ((aiAvgEngagement - manualAvgEngagement) / manualAvgEngagement * 100)
    : 0;

  const sortedAnalytics = [...analytics].sort((a, b) => {
    if (sortBy === 'engagement_rate') return (b.engagement_rate || 0) - (a.engagement_rate || 0);
    if (sortBy === 'impressions') return (b.impressions || 0) - (a.impressions || 0);
    if (sortBy === 'revenue') return (b.revenue_generated || 0) - (a.revenue_generated || 0);
    return 0;
  });

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown';
  };

  const formatRevenue = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'var(--bg-primary)', zIndex: 1000,
      overflow: 'auto', animation: 'fadeIn 0.2s ease'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
              📊 Performance Analytics
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
              Track your content's impact and revenue
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px', borderRadius: '10px',
              border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
              color: 'var(--text-secondary)', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit'
            }}
          >
            ← Back
          </button>
        </div>

        {/* Time Range Selector */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          {['7d', '30d', 'all'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              style={{
                padding: '8px 16px', borderRadius: '8px',
                border: `2px solid ${timeRange === range ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                background: timeRange === range ? 'var(--accent-subtle-bg)' : 'transparent',
                color: timeRange === range ? 'var(--accent-primary)' : 'var(--text-secondary)',
                fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              {range === '7d' ? 'Last 7 days' : range === '30d' ? 'Last 30 days' : 'All time'}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ width: '40px', height: '40px', border: '3px solid var(--border-color)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <p style={{ color: 'var(--text-secondary)' }}>Loading analytics...</p>
          </div>
        ) : (
          <>
            {/* Key Metrics Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '32px' }}>
              <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '24px', border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '8px' }}>Total Impressions</div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  {formatNumber(totalImpressions)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Across {analytics.length} post{analytics.length !== 1 ? 's' : ''}
                </div>
              </div>

              <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '24px', border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '8px' }}>Avg Engagement Rate</div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--success)', marginBottom: '4px' }}>
                  {avgEngagementRate.toFixed(1)}%
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {formatNumber(totalEngagement)} total engagements
                </div>
              </div>

              <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '24px', border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '8px' }}>Total Revenue</div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--accent-primary)', marginBottom: '4px' }}>
                  {formatRevenue(totalRevenue)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  From tracked posts
                </div>
              </div>

              {aiPosts.length > 0 && manualPosts.length > 0 && manualAvgEngagement > 0 && (
                <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '24px', border: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '8px' }}>AI vs Manual</div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: aiVsManualPct >= 0 ? 'var(--success)' : 'var(--warning)', marginBottom: '4px' }}>
                    {aiVsManualPct >= 0 ? '+' : ''}{aiVsManualPct.toFixed(0)}%
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {aiVsManualPct >= 0 ? 'AI posts perform better' : 'Manual posts leading'}
                  </div>
                </div>
              )}
            </div>

            {/* Posts Table */}
            <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>Post Performance</h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Sort by:</span>
                  {['engagement_rate', 'impressions', 'revenue'].map(sort => (
                    <button
                      key={sort}
                      onClick={() => setSortBy(sort)}
                      style={{
                        padding: '4px 10px', borderRadius: '6px',
                        border: 'none',
                        background: sortBy === sort ? 'var(--accent-subtle-bg)' : 'transparent',
                        color: sortBy === sort ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        fontSize: '12px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit'
                      }}
                    >
                      {sort === 'engagement_rate' ? 'Engagement' : sort === 'impressions' ? 'Reach' : 'Revenue'}
                    </button>
                  ))}
                </div>
              </div>

              {sortedAnalytics.length === 0 ? (
                <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📈</div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '16px', marginBottom: '8px' }}>No analytics data yet</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                    Start reporting performance on your posts to see insights here
                  </p>
                </div>
              ) : (
                <div style={{ overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-secondary)' }}>
                        <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Post</th>
                        <th style={{ padding: '12px 24px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Impressions</th>
                        <th style={{ padding: '12px 24px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Engagement</th>
                        <th style={{ padding: '12px 24px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Rate</th>
                        <th style={{ padding: '12px 24px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Revenue</th>
                        <th style={{ padding: '12px 24px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Posted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAnalytics.map((post) => (
                        <tr key={post.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td style={{ padding: '16px 24px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '4px' }}>
                              {post.project_id ? getProjectName(post.project_id) : 'Manual Post'}
                            </div>
                            {post.project_id && (
                              <div style={{ fontSize: '12px', color: 'var(--success)' }}>✨ AI-optimized</div>
                            )}
                          </td>
                          <td style={{ padding: '16px 24px', textAlign: 'right', fontSize: '14px', color: 'var(--text-primary)', fontWeight: '500' }}>
                            {formatNumber(post.impressions || 0)}
                          </td>
                          <td style={{ padding: '16px 24px', textAlign: 'right', fontSize: '14px', color: 'var(--text-secondary)' }}>
                            {formatNumber((post.likes || 0) + (post.retweets || 0) + (post.replies || 0))}
                          </td>
                          <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                            <span style={{
                              fontSize: '14px', fontWeight: '600',
                              color: (post.engagement_rate || 0) >= 5 ? 'var(--success)' : (post.engagement_rate || 0) >= 2 ? 'var(--warning)' : 'var(--error)'
                            }}>
                              {(post.engagement_rate || 0).toFixed(1)}%
                            </span>
                          </td>
                          <td style={{ padding: '16px 24px', textAlign: 'right', fontSize: '14px', color: 'var(--accent-primary)', fontWeight: '500' }}>
                            {post.revenue_generated ? formatRevenue(post.revenue_generated) : '-'}
                          </td>
                          <td style={{ padding: '16px 24px', textAlign: 'right', fontSize: '13px', color: 'var(--text-muted)' }}>
                            {post.posted_at ? new Date(post.posted_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
